// 内部片（`_` 前缀）：AI 调用日志的阶段判定/延迟推导/配色，供 AiLogView、_AiPipeline 与入口共用。
// 均为 AI 调用日志的内部实现，不对外导出（入口不重导出本文件）。

import { fmtSpan } from '../../utils/format';
import { getIntentName } from '../../utils/intentName';
import { PRIMARY, CYAN, PURPLE, WARNING, DANGER, NEUTRAL, TEAL } from '../../theme/palette';
import type { PillColor } from '../PillTag';
import type { AICallLog } from '../../api/cdr';

/**
 * 影子对照行（意图识别 A/B 留痕 stage=intent_shadow）：一行里同时有 v1/v2 两条判定，
 * 属诊断数据而非识别结果——不得参与「意图归属 / 识别耗时」推导，也不能当作跳意图词表的键
 * （其 content 是 v2 的判定 code，可能不在词表里）。
 */
export function isShadowStage(stage: string): boolean {
  return stage === 'intent_shadow';
}

/** 是否为实时识别 LLM 产出的阶段（analyzeRealtime 使用独立 realtimeLLM 做情绪/风险/意图分析） */
export function isRealtimeStage(stage: string): boolean {
  if (isShadowStage(stage)) return false;
  return stage === 'session_intent' || stage === 'realtime_transfer' || stage.startsWith('intent_');
}

/** 意图识别日志：detail 内带 intent_name（动作名称）时返回之（content 仍为 code），否则经词表映射中文名 */
export function intentDisplay(log: AICallLog): string {
  if (log.stage === 'session_intent') {
    try {
      const d = JSON.parse(log.detail || '{}');
      if (d && d.intent_name) return String(d.intent_name);
    } catch {
      /* detail 非 JSON 忽略 */
    }
  }
  return getIntentName(log.content);
}

/**
 * content 不是意图归一码的 intent_* 阶段：动作名（intent_query）/ 字段名（intent_collect_dtmf）/
 * 客户原话（intent_invalid_question）/ 降级留痕（intent_v2_fallback）。
 * 它们不是识别结果，不得当意图行——否则会被高亮、并把动作名当 code 跳向不存在的词表条目。
 */
const NON_CODE_INTENT_STAGES = new Set([
  'intent_query',
  'intent_collect_dtmf',
  'intent_invalid_question',
  'intent_v2_fallback',
]);

/**
 * 是否意图识别行（实时识别判定 + 由其派生的动作/守卫）：
 * content 通常是意图归一码（= ai_intent_vocab.code），可作「跳意图词表定位该条配置」的键；
 * 但 intent_ 前缀不保证 content 是码（见 NON_CODE_INTENT_STAGES），故按前缀判定后再排除。
 * 真正可否跳转另由 isIntentCode 判定（session_intent 的附和行 content 也不是码）。
 */
export function isIntentLog(stage: string): boolean {
  if (isShadowStage(stage)) return false;
  if (NON_CODE_INTENT_STAGES.has(stage)) return false;
  return stage === 'session_intent' || stage.startsWith('intent_');
}

/**
 * content 是否为意图归一码：命中意图词表才可用于跳转。
 * 词表未加载时恒为 false（保守不跳），加载后由 useIntentNames 触发重渲染重新判定。
 */
export function isIntentCode(code?: string | null): boolean {
  const c = (code || '').trim();
  return !!c && getIntentName(c) !== c;
}

/** 阶段 → 时间线配色（主对话 LLM=紫 / 实时识别 LLM=青绿 / asr=蓝 / tts=青 / vc=橙 / 流程=红 / 其他=灰）
 *  vc 只出现在历史通话的 ai_call_log（现行链路不产生该阶段），配色留着是为了旧通话仍读得懂。 */
export function aiStageColor(stage: string): string {
  if (stage.startsWith('asr') || stage === 'barge_in') return 'rgb(var(--arcoblue-6))';
  // 实时识别先于流程色判断：intent_transfer/realtime_transfer 含 transfer 不应染红
  if (isRealtimeStage(stage)) return TEAL;
  if (stage.startsWith('llm')) return PURPLE;
  if (stage.startsWith('asr')) return PRIMARY;
  if (stage.startsWith('tts')) return CYAN;
  if (stage.startsWith('vc')) return WARNING;
  if (stage.includes('transfer') || stage.includes('hangup') || stage === 'survey_request') return DANGER;
  return NEUTRAL;
}

/** 阶段 → 胶囊语义色（与 aiStageColor 同档：asr/抢话=蓝、实时识别与主对话 LLM/TTS=青、变声=橙、流程/挂断=红、其他=灰） */
export function aiStagePill(stage: string): PillColor {
  if (stage.startsWith('asr') || stage === 'barge_in') return 'arcoblue';
  if (isRealtimeStage(stage)) return 'cyan';
  if (stage.startsWith('llm') || stage.startsWith('tts')) return 'cyan';
  if (stage.startsWith('vc')) return 'orange';
  if (stage.includes('transfer') || stage.includes('hangup') || stage === 'survey_request') return 'red';
  return 'gray';
}

/** 取 detail（空格分隔的 key=value 串）里形如 key=123ms 的耗时毫秒；无该键或值非耗时返回 null */
export function detailDurationMs(detail: string | undefined, key: string): number | null {
  const m = new RegExp(`(?:^|\\s)${key}=(\\d+)ms(?=\\s|$)`).exec(detail || '');
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : null;
}

/**
 * LLM/TTS 响应延迟（毫秒），key=日志行 id，一行可挂多条指标：
 * - LLM 响应：最近一次 llm_start 到 llm_return 的偏移差；
 * - 旧口径 TTS：最近一次 tts_start 到 tts_first_frame/tts_end 的偏移差（库中历史通话）；
 * - 新口径 TTS：轮/段行 detail 的 first=该轮（段）首帧音频延迟、total=整轮耗时，直接取用。
 */
export function computeLatency(logs: AICallLog[]): Record<number, { label: string; ms: number }[]> {
  const lat: Record<number, { label: string; ms: number }[]> = {};
  const push = (id: number, label: string, ms: number) => {
    if (!(ms >= 0)) return;
    if (!lat[id]) lat[id] = [];
    lat[id].push({ label, ms });
  };
  let llmStart = -1;
  let ttsStart = -1;
  for (const l of logs) {
    const off = Number(l.offset_ms) || 0;
    if (l.stage === 'llm_start') {
      llmStart = off;
    } else if (l.stage === 'llm_return' && llmStart >= 0) {
      push(l.id, 'LLM 响应', off - llmStart);
    } else if (l.stage === 'tts_start') {
      ttsStart = off;
    } else if ((l.stage === 'tts_first_frame' || l.stage === 'tts_end') && ttsStart >= 0) {
      push(l.id, l.stage === 'tts_first_frame' ? 'TTS 首帧' : 'TTS 总耗', off - ttsStart);
    } else if (l.stage === 'tts_turn' || l.stage === 'tts_segment') {
      const first = detailDurationMs(l.detail, 'first');
      if (first !== null) push(l.id, 'TTS 首帧', first);
      if (l.stage === 'tts_turn') {
        const total = detailDurationMs(l.detail, 'total');
        if (total !== null) push(l.id, 'TTS 总耗', total);
      }
    }
  }
  return lat;
}

/**
 * 语音起止文案：begin_ms/end_ms 为录音轴说话起止（与转写分段同轴）。
 * 两列皆为 0 = 该阶段无语音语义（llm/intent）或历史行无该数据 → 不显示（避免出现「说话 00:00」）。
 */
export function logSpanText(beginMs?: number, endMs?: number): string {
  const b = Number(beginMs) || 0;
  const e = Number(endMs) || 0;
  if (b <= 0 && e <= 0) return '';
  return fmtSpan(b > 0 ? b : undefined, e > 0 ? e : undefined);
}

/**
 * 机器人播报起止（录音轴毫秒，渲染为时间线的「播报」胶囊）：
 * - tts_segment 行自带该段区间；tts_turn 行自带整轮区间（首段出声 → 末段结束/被打断），均直接取用；
 * - 旧口径 tts_first_frame（begin_ms=真实出声时刻）与 tts_end（end_ms=播完/被打断时刻）分两行，
 *   须按相邻配对还原区间并挂到 tts_end 行（配对方式与 computeLatency 的旧 TTS 延迟一致）；
 *   首帧缺失（合成失败/零帧）时只给结束时刻，渲染为单时间。
 */
export function computeSpeakSpans(logs: AICallLog[]): Record<number, { beginMs?: number; endMs?: number }> {
  const spans: Record<number, { beginMs?: number; endMs?: number }> = {};
  let begin = 0;
  for (const l of logs) {
    const b = Number(l.begin_ms) || 0;
    const e = Number(l.end_ms) || 0;
    if (l.stage === 'tts_segment') {
      if (b > 0 || e > 0) spans[l.id] = { beginMs: b > 0 ? b : undefined, endMs: e > 0 ? e : undefined };
    } else if (l.stage === 'tts_turn') {
      if (b > 0 && e > 0) spans[l.id] = { beginMs: b, endMs: e };
    } else if (l.stage === 'tts_first_frame' && b > 0) {
      begin = b;
    } else if (l.stage === 'tts_end' && e > 0) {
      spans[l.id] = begin > 0 ? { beginMs: begin, endMs: e } : { endMs: e };
      begin = 0;
    }
  }
  return spans;
}
